import { LedgerNoteEditableCell } from "@/features/management-ledger/components/LedgerNoteEditableCell";
import { LedgerJobChangeBanPeriodCell } from "@/features/management-ledger/components/LedgerJobChangeBanPeriodCell";
import {
  LedgerEmploymentPeriodEditableCell,
  TUpdateEmploymentPeriodDraft,
} from "@/features/management-ledger/components/LedgerEmploymentPeriodEditableCell";
import { LedgerValidPeriodEditableCell, TUpdateValidPeriodDraft } from "@/features/management-ledger/components/LedgerValidPeriodEditableCell";
import {
  resolveDraftValue,
  TEmploymentPeriodDrafts,
  TLedgerDraftEdits,
  TUpdateLedgerDraft,
} from "@/features/management-ledger/constants/job-ledger-columns";
import { TJobseekerLedgerRow } from "@/features/management-ledger/types/management-ledger";
import { Box } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
export const JOBSEEKER_LEDGER_PINNED_COLUMN_ID = "jobseekerInfo";
const multiLineHeadProps = {
  className: "ledger-multiline-head",
  sx: { "& .Mui-TableHeadCell-Content-Wrapper": { whiteSpace: "pre-line" as const } },
};
const subColumnHeadProps = { className: "ledger-sub-col", sx: { verticalAlign: "middle !important" } };
const leafColumnHeadProps = { className: "ledger-leaf-col", sx: { verticalAlign: "middle !important" } };
const talentCellMergeProps = {
  muiTableBodyCellProps: ({
    row,
  }: {
    row: {
      original: TJobseekerLedgerRow;
    };
  }) =>
    row.original.talentRowSpan === 0 ? { sx: { display: "none" } } : { rowSpan: row.original.talentRowSpan, sx: { verticalAlign: "top" as const } },
};
export type TValidPeriodDrafts = Record<number, string>;

export const getJobseekerLedgerColumns = (
  draftEdits: TLedgerDraftEdits,
  updateDraft: TUpdateLedgerDraft,
  isEditing: boolean,
  validPeriodDrafts: TValidPeriodDrafts,
  updateValidPeriodDraft: TUpdateValidPeriodDraft,
  employmentPeriodDrafts: TEmploymentPeriodDrafts,
  updateEmploymentPeriodDraft: TUpdateEmploymentPeriodDraft,
  showValidationError?: boolean,
): MRT_ColumnDef<TJobseekerLedgerRow>[] => [
  {
    id: JOBSEEKER_LEDGER_PINNED_COLUMN_ID,
    accessorKey: "jobseekerInfo",
    header: "①求職者氏名\n②住所\n③生年月日",
    size: 240,
    muiTableHeadCellProps: multiLineHeadProps,
    ...talentCellMergeProps,
    Cell: ({ cell }) => <Box sx={{ whiteSpace: "pre-line" }}>{cell.getValue<string>()}</Box>,
  },
  { accessorKey: "desiredOccupation", header: "④希望職種", size: 140, ...talentCellMergeProps },
  { accessorKey: "receptionDate", header: "⑤受付年月日", size: 120, ...talentCellMergeProps },
  {
    accessorKey: "validPeriod",
    header: "⑥有効期間",
    size: 180,
    muiTableHeadCellProps: { sx: { width: 180, maxWidth: 180 } },
    muiTableBodyCellProps: (props: {
      row: {
        original: TJobseekerLedgerRow;
      };
    }) => {
      const merged = talentCellMergeProps.muiTableBodyCellProps(props);
      return { ...merged, sx: { ...merged.sx, width: 180, maxWidth: 180, overflow: "visible" } };
    },
    Cell: ({ row }) => (
      <LedgerValidPeriodEditableCell
        row={row.original}
        value={validPeriodDrafts[row.original.talentId] ?? row.original.validPeriod}
        isEditing={isEditing}
        showValidationError={showValidationError}
        onChange={updateValidPeriodDraft}
      />
    ),
  },
  {
    id: "referralStatus",
    header: "⑦職業紹介の取扱状況",
    columns: [
      { accessorKey: "referralDate", header: "紹介年月日", size: 120, muiTableHeadCellProps: subColumnHeadProps },
      { accessorKey: "jobManagementNo", header: "求人管理番号", size: 120, muiTableHeadCellProps: subColumnHeadProps },
      { accessorKey: "employerName", header: "求人者の氏名又は名称", size: 180, muiTableHeadCellProps: subColumnHeadProps },
      { accessorKey: "employerAddress", header: "所在地", size: 160, muiTableHeadCellProps: subColumnHeadProps },
      { accessorKey: "hiringResult", header: "採否結果", size: 100, muiTableHeadCellProps: subColumnHeadProps },
      { accessorKey: "hiredDate", header: "採用年月日", size: 120, muiTableHeadCellProps: subColumnHeadProps },
      {
        accessorKey: "employmentPeriod",
        header: "雇用期間",
        size: 180,
        muiTableHeadCellProps: { ...subColumnHeadProps, sx: { ...subColumnHeadProps.sx, width: 180, maxWidth: 180 } },
        muiTableBodyCellProps: { sx: { width: 180, maxWidth: 180 } },
        Cell: ({ row }) =>
          row.original.caseMatchId == null ? null : (
            <LedgerEmploymentPeriodEditableCell
              entityId={row.original.caseMatchId}
              value={employmentPeriodDrafts[row.original.caseMatchId] ?? row.original.employmentPeriod}
              isEditing={isEditing}
              onChange={updateEmploymentPeriodDraft}
            />
          ),
      },
      {
        id: "permanentEmploymentGroup",
        header: "無期雇用就職者に関する事項等",
        columns: [
          {
            accessorKey: "permanentEmploymentNote",
            header: "転職勧奨禁止期間",
            size: 170,
            muiTableHeadCellProps: leafColumnHeadProps,
            muiTableBodyCellProps: { sx: { whiteSpace: "normal", wordBreak: "break-word" } },
            Cell: ({ row }) => (
              <LedgerJobChangeBanPeriodCell
                row={row.original}
                value={resolveDraftValue(draftEdits, row.original.caseMatchId, "permanentEmploymentNote", row.original.permanentEmploymentNote)}
                isEditing={isEditing}
                onChange={updateDraft}
              />
            ),
          },
          {
            accessorKey: "separationStatusNote",
            header: "6か月以内の離職状況",
            size: 228,
            muiTableHeadCellProps: leafColumnHeadProps,
            muiTableBodyCellProps: { sx: { whiteSpace: "normal", wordBreak: "break-word" } },
            Cell: ({ row }) => (
              <LedgerNoteEditableCell
                row={row.original}
                field="separationStatusNote"
                value={resolveDraftValue(draftEdits, row.original.caseMatchId, "separationStatusNote", row.original.separationStatusNote)}
                isEditing={isEditing}
                onChange={updateDraft}
              />
            ),
          },
        ],
      },
      {
        accessorKey: "remarks",
        header: "備考",
        size: 200,
        muiTableHeadCellProps: subColumnHeadProps,
        muiTableBodyCellProps: { sx: { whiteSpace: "normal", wordBreak: "break-word" } },
        Cell: ({ row }) => (
          <LedgerNoteEditableCell
            row={row.original}
            field="remarks"
            value={resolveDraftValue(draftEdits, row.original.caseMatchId, "remarks", row.original.remarks)}
            isEditing={isEditing}
            onChange={updateDraft}
          />
        ),
      },
    ],
  },
];
